import CategoryIcon from "@mui/icons-material/Category";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TableCard from "../../components/TableCard";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import {
  atualizarCategoriasTributarias,
  buscarCategoriasTributarias,
  buscarCategoriasTributariasPorTexto,
  deletarCategoriasTributarias,
  inserirCategoriasTributarias,
} from "../../services/api-planejamento-tributario.service";
import SnackBar from "../../components/SnackBar";
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import WhiteButton from "../../components/WhiteButton";
import BlueButton from "../../components/BlueButton";
import { buscarComboPorCodigo } from "../../services/auth.service";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import Loading from "../../components/Loading";

const CategoriasTributarias = () => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [msg, setMsg] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState();
  const [formData, setFormData] = useState({
    idCategoriaTributaria: null,
    descricao: "",
    tiposPrevia: [],
    tiposNegocio: [],
  });
  const [comboTiposPrevia, setComboTiposPrevia] = useState([]);
  const [comboTiposNegocio, setComboTiposNegocio] = useState([]);
  const [dados, setDados] = useState();
  const [isUpdate, setIsUpdate] = useState(false);

  const columns = ["ID Categoria", "Descrição"];

  // Utilitário: retorna as prévias permitidas dado um conjunto de IDs de tipos de negócio
  const getAllowedPrevias = (tiposNegocioIds) => {
    if (!Array.isArray(comboTiposPrevia) || comboTiposPrevia.length === 0)
      return [];
    if (!Array.isArray(tiposNegocioIds) || tiposNegocioIds.length === 0)
      return comboTiposPrevia;
    return comboTiposPrevia.filter(
      (tp) =>
        Array.isArray(tp.tiposNegocio) &&
        tp.tiposNegocio.some((id) => tiposNegocioIds.includes(id))
    );
  };

  const handleDadosCategorias = async () => {
    try {
      setLoading(true);
      const response = await buscarCategoriasTributarias();
      setData(await handleOrganizaData(response.categories));
    } catch (error) {
      setObjSnackBar({ type: "error", msg: error.response?.data?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await handleDadosCategorias();

      const [responseTiposPrevia, responseTiposNegocio] = await Promise.all([
        buscarComboPorCodigo("TIPOSPREVIA"),
        buscarComboPorCodigo("TIPOSNEGOCIO"),
      ]);

      const tiposPrevia = responseTiposPrevia.detalhes.itensCombo.map(
        (item) => {
          const partes = item.split("/|/");
          return {
            id: Number(partes[0]),
            detalhes: partes[1],
            tiposNegocio: partes[2]
              ? partes[2]
                  .replace(/[{}]/g, "")
                  .split(",")
                  .filter(Boolean)
                  .map(Number)
              : [],
          };
        }
      );

      const tiposNegocio = responseTiposNegocio.detalhes.itensCombo.map(
        (item) => {
          const [id, detalhes] = item.split("/|/");
          return { id: Number(id), detalhes };
        }
      );

      setComboTiposPrevia(tiposPrevia);
      setComboTiposNegocio(tiposNegocio);
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!openEditModal && dados) {
      setLoading(true);

      const tiposPreviaSafe = dados.tiposPrevia || [];
      const tiposNegocioSafe = dados.tiposNegocio || [];

      const tiposPreviaSelected = comboTiposPrevia.filter((item) =>
        tiposPreviaSafe.includes(Number(item.id))
      );
      const tiposNegocioSelected = comboTiposNegocio.filter((item) =>
        tiposNegocioSafe.includes(Number(item.id))
      );

      // Garantir consistência: só manter prévias compatíveis com os negócios selecionados
      const allowed = getAllowedPrevias(tiposNegocioSelected.map((n) => n.id));
      const consistentes = tiposPreviaSelected.filter((tp) =>
        allowed.some((a) => a.id === tp.id)
      );

      setFormData({
        idCategoriaTributaria: dados.idCategoriaTributaria,
        descricao: dados.descricao,
        tiposPrevia: consistentes,
        tiposNegocio: tiposNegocioSelected,
      });

      setOpenEditModal(true);
      setLoading(false);
    }
  }, [comboTiposPrevia, comboTiposNegocio, dados]);

  const handleSnackBar = (obj) => setObjSnackBar(obj);

  const handleOpenEditModal = (dados) => {
    if (dados) {
      setDados(dados);
    } else {
      setDados({
        idCategoriaTributaria: null,
        descricao: "",
        tiposPrevia: [],
        tiposNegocio: [],
      });
    }
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setIsUpdate(false);
    setDados();
    setFormData({
      idCategoriaTributaria: null,
      descricao: "",
      tiposPrevia: [],
      tiposNegocio: [],
    });
  };

  const handleDescricaoChange = (e) => {
    setFormData({ ...formData, descricao: e.target.value });
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const handleTiposPreviaChange = (e) => {
    const uniqueValues = e.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );
    setFormData({ ...formData, tiposPrevia: uniqueValues });
  };

  const handleTiposNegocioChange = (e) => {
    const uniqueValues = e.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );

    const selectedNegocioIds = uniqueValues.map((n) => n.id);
    const allowedPrevias = getAllowedPrevias(selectedNegocioIds);

    // Interseção: mantém apenas as prévias já selecionadas que continuam válidas
    const newTiposPrevia = (formData.tiposPrevia || []).filter((tp) =>
      allowedPrevias.some((a) => a.id === tp.id)
    );

    setFormData({
      ...formData,
      tiposNegocio: uniqueValues,
      tiposPrevia: newTiposPrevia,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      // Segurança adicional no client: filtrar prévias inconsistentes antes de enviar
      const selectedNegocioIds = (formData.tiposNegocio || []).map((n) => n.id);
      const allowedPrevias = getAllowedPrevias(selectedNegocioIds);
      const sanitizedPrevias = (formData.tiposPrevia || []).filter((tp) =>
        allowedPrevias.some((a) => a.id === tp.id)
      );

      if (
        !formData.descricao ||
        sanitizedPrevias.length === 0 ||
        selectedNegocioIds.length === 0
      ) {
        return setObjSnackBar({
          type: "error",
          msg: "Todos os campos devem ser preenchidos!",
        });
      }

      const payload = {
        ...formData,
        tiposPrevia: sanitizedPrevias.map((item) => item.id),
        tiposNegocio: (formData.tiposNegocio || []).map((item) => item.id),
      };
      const idCategoriaTributaria = payload.idCategoriaTributaria;
      delete payload.idCategoriaTributaria;

      const response = isUpdate
        ? await atualizarCategoriasTributarias(idCategoriaTributaria, payload)
        : await inserirCategoriasTributarias(payload);

      setObjSnackBar({
        type: "success",
        msg: response.message || response.mensagem,
      });
      handleDadosCategorias();
    } catch (error) {
      setObjSnackBar({ type: "error", msg: error.response?.data?.message });
    }
    handleCloseEditModal();
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    try {
      const response = await deletarCategoriasTributarias(
        formData.idCategoriaTributaria
      );
      setObjSnackBar({ type: "success", msg: response.message });
      handleDadosCategorias();
    } catch (error) {
      setObjSnackBar({ type: "error", msg: error.response?.data?.mensagem });
    }
    handleCloseDeleteModal();
  };

  const handleDebounce = debounce((value) => {
    handlePesquisaPorTexto(value);
  }, 500);

  const handlePesquisaPorTexto = async (value) => {
    try {
      setLoading(true);
      const response =
        value === ""
          ? await buscarCategoriasTributarias()
          : await buscarCategoriasTributariasPorTexto(value);
      setData(await handleOrganizaData(response.categories));
    } catch (error) {
      setObjSnackBar({ type: "error", msg: error.response?.data?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizaData = async (response) => {
    return response.map((item) => {
      return {
        idCategoriaTributaria: item.idCategoriaTributaria,
        descricao: item.descricao,
        botoes: [
          {
            icon: <EditIcon />,
            title: "EDITAR",
            onClick: () => {
              setIsUpdate(true);
              handleOpenEditModal(item);
            },
          },
          {
            icon: <DeleteIcon />,
            title: "EXCLUIR",
            onClick: () => {
              setFormData(item);
              setOpenDeleteModal(true);
            },
          },
        ],
      };
    });
  };

  // Opções mostradas no Autocomplete de prévias, de acordo com os tipos de negócio selecionados
  const tiposNegocioSelecionados = formData.tiposNegocio.map((t) => t.id);
  const filteredTiposPrevia = getAllowedPrevias(tiposNegocioSelecionados);

  return (
    <>
      <TableCard
        loading={loading}
        iconHeader={<CategoryIcon fontSize="1rem" />}
        titleHeader="Categorias Tributárias"
        data={data}
        setData={setData}
        columns={["ID Categoria", "Descrição"]}
        textHeaderCard="descricao"
        handleDebounce={handleDebounce}
        temInsert
        insertOpenModal={handleOpenEditModal}
      />

      <Dialog open={openEditModal} onClose={handleCloseEditModal}>
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <form onSubmit={handleSave}>
            <DialogTitle>
              {isUpdate ? "Edição" : "Cadastro"} de Categoria Tributária
            </DialogTitle>
            <DialogContent>
              {isUpdate && (
                <TextField
                  label="ID Categoria Tributária"
                  value={formData.idCategoriaTributaria}
                  fullWidth
                  size="small"
                  margin="normal"
                  disabled
                />
              )}

              <TextField
                label="Descrição"
                value={formData.descricao}
                required
                onChange={handleDescricaoChange}
                fullWidth
                size="small"
                margin="normal"
              />

              <Autocomplete
                sx={{ marginTop: "1rem" }}
                multiple
                size="small"
                options={comboTiposNegocio}
                value={formData.tiposNegocio}
                disableCloseOnSelect
                getOptionLabel={(option) => option.detalhes}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => handleTiposNegocioChange(value)}
                renderInput={(params) => (
                  <TextField {...params} label={`Tipos de Negócios *`} />
                )}
              />

              <Autocomplete
                sx={{ marginTop: "1rem" }}
                multiple
                size="small"
                options={filteredTiposPrevia}
                value={formData.tiposPrevia}
                disableCloseOnSelect
                getOptionLabel={(option) => option.detalhes}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => handleTiposPreviaChange(value)}
                renderInput={(params) => (
                  <TextField {...params} label={`Tipos de Prévia *`} />
                )}
              />
            </DialogContent>
            <DialogActions>
              <WhiteButton
                clickFunction={handleCloseEditModal}
                textValue="Cancelar"
              />
              <BlueButton textValue="Salvar" type="submit" />
            </DialogActions>
          </form>
        )}
      </Dialog>

      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <form onSubmit={handleDelete}>
            <DialogTitle>{`Deseja realmente excluir a categoria tributária "${formData.descricao}"?`}</DialogTitle>
            <DialogActions>
              <WhiteButton
                clickFunction={() => setOpenDeleteModal(false)}
                textValue="Cancelar"
              />
              <BlueButton textValue="Sim" type="submit" />
            </DialogActions>
          </form>
        )}
      </Dialog>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={handleSnackBar} />
    </>
  );
};

export default CategoriasTributarias;
